import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ExternalLink, ArrowRight, ArrowLeft, CreditCard, Webhook, ShieldCheck, AlertTriangle, RefreshCw, Eye, EyeOff } from "lucide-react";

const STEPS = [
  { id: "key", title: "API Key" },
  { id: "account", title: "Account Status" },
  { id: "webhook", title: "Webhook" },
  { id: "test", title: "Test Charge" },
];

function StepHeader({ current }) {
  return (
    <div className="flex items-center justify-between mb-6 overflow-x-auto pb-2" data-testid="stripe-wizard-stepper">
      {STEPS.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={s.id} className="flex items-center shrink-0">
            <div className={`flex items-center gap-2 ${active ? "text-deep-blue" : done ? "text-green-600" : "text-slate-400"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-deep-blue text-white" : done ? "bg-green-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs font-medium hidden sm:inline ${active ? "text-deep-blue" : done ? "text-green-600" : "text-slate-400"}`}>{s.title}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`w-6 sm:w-10 h-px mx-2 ${done ? "bg-green-300" : "bg-slate-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function CheckRow({ ok, label, detail }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-b-0">
      {ok
        ? <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
        : <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />}
      <div className="flex-1">
        <div className="font-medium text-sm text-slate-800">{label}</div>
        {detail && <div className="text-xs text-slate-500 mt-0.5">{detail}</div>}
      </div>
    </div>
  );
}

function ModeBadge({ mode }) {
  if (!mode || mode === "missing") return <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-bold">NOT SET</span>;
  if (mode === "live") return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold" data-testid="stripe-mode-live">LIVE</span>;
  if (mode === "test") return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold" data-testid="stripe-mode-test">TEST</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">{mode.toUpperCase()}</span>;
}

export default function StripeWizard({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // Step 0 — Key
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [keyValidating, setKeyValidating] = useState(false);
  const [keyResult, setKeyResult] = useState(null);

  // Step 2 — Webhook
  const [creatingWebhook, setCreatingWebhook] = useState(false);
  const [webhookResult, setWebhookResult] = useState(null);
  const [webhookUrlOverride, setWebhookUrlOverride] = useState("");

  // Step 3 — Test Charge
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/stripe/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Stripe status");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) {
      setStep(0); setApiKey(""); setKeyResult(null); setWebhookResult(null); setTestResult(null);
      fetchStatus();
    }
  }, [open]);

  const validateKey = async () => {
    if (!apiKey.trim()) { toast.error("Paste your Stripe secret key first"); return; }
    setKeyValidating(true); setKeyResult(null);
    try {
      const res = await axios.post(`${API}/settings/stripe/validate-key`, { api_key: apiKey.trim() }, { withCredentials: true });
      setKeyResult(res.data);
      if (!res.data.ok) toast.error(res.data.detail);
    } catch (e) {
      setKeyResult({ ok: false, detail: e.response?.data?.detail || "Network error" });
    } finally { setKeyValidating(false); }
  };

  const saveKey = async () => {
    setKeyValidating(true);
    try {
      await axios.post(`${API}/settings/stripe/save-key`, { api_key: apiKey.trim() }, { withCredentials: true });
      toast.success("Stripe key saved");
      await fetchStatus();
      setApiKey(""); setKeyResult(null);
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setKeyValidating(false); }
  };

  const createWebhook = async () => {
    setCreatingWebhook(true); setWebhookResult(null);
    try {
      const payload = webhookUrlOverride.trim() ? { url: webhookUrlOverride.trim() } : {};
      const res = await axios.post(`${API}/settings/stripe/create-webhook`, payload, { withCredentials: true });
      setWebhookResult(res.data);
      toast.success("Webhook created and signing secret saved");
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Webhook creation failed");
      setWebhookResult({ success: false, error: e.response?.data?.detail });
    } finally { setCreatingWebhook(false); }
  };

  const runTestCharge = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await axios.post(`${API}/settings/stripe/test-charge`, {}, { withCredentials: true });
      setTestResult(res.data);
      if (res.data.success) toast.success("Stripe connectivity confirmed");
      else toast.error(res.data.error || "Test charge failed");
    } catch (e) {
      setTestResult({ success: false, error: e.response?.data?.detail || "Network error" });
      toast.error("Test charge failed");
    } finally { setTesting(false); }
  };

  const mode = status?.mode || "missing";
  const acct = status?.account;
  const webhookConfigured = status?.webhooks?.some(w => w.is_target);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="stripe-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-deep-blue">
            <CreditCard className="w-5 h-5" /> Stripe Setup Wizard
            <ModeBadge mode={mode} />
            {status?.key_last4 && <span className="text-xs text-slate-400 font-mono">…{status.key_last4}</span>}
            <Button size="sm" variant="ghost" onClick={fetchStatus} className="ml-auto" data-testid="stripe-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <StepHeader current={step} />

        {loading && !status && (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        )}

        {status && (
          <>
            {/* ============ STEP 0: API KEY ============ */}
            {step === 0 && (
              <div className="space-y-4" data-testid="stripe-step-key">
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-slate-700">
                  <strong>Step 1 of 4 — API Key.</strong> Paste your Stripe secret key. Live keys start with <code className="text-xs">sk_live_…</code>; test keys start with <code className="text-xs">sk_test_…</code>.
                  <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 underline ml-2">Get your key <ExternalLink className="w-3 h-3" /></a>
                </div>

                {status.key_configured && (
                  <div className="bg-green-50 border border-green-200 rounded p-3 flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <div>Key already configured (<ModeBadge mode={mode} /> …{status.key_last4}). Replace it below or skip to next step.</div>
                  </div>
                )}

                <div>
                  <Label className="text-sm font-medium">Stripe Secret Key</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type={showKey ? "text" : "password"}
                      placeholder="sk_live_…"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      className="font-mono text-xs"
                      data-testid="stripe-key-input"
                    />
                    <Button variant="outline" size="icon" onClick={() => setShowKey(!showKey)} data-testid="stripe-key-toggle">
                      {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Stored encrypted in DB. Never shared with the browser after save.</p>
                </div>

                {keyResult && (
                  <div className={`rounded p-3 text-sm ${keyResult.ok ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
                    {keyResult.ok ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 font-medium text-green-800"><CheckCircle2 className="w-4 h-4" /> Key validated</div>
                        <div className="text-xs text-slate-600">Mode: <strong>{keyResult.mode?.toUpperCase()}</strong>{keyResult.account_id && <> · Account: <code>{keyResult.account_id}</code></>}</div>
                        {!keyResult.charges_enabled && <div className="text-xs text-amber-700 mt-1">⚠ Charges are NOT enabled on this account. Finish Stripe activation before going live.</div>}
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 text-red-800"><XCircle className="w-4 h-4 mt-0.5 shrink-0" /><div>{keyResult.detail}</div></div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 justify-between pt-2">
                  <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={validateKey} disabled={!apiKey.trim() || keyValidating} data-testid="stripe-validate-key-btn">
                      {keyValidating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Validate
                    </Button>
                    <Button onClick={saveKey} disabled={!keyResult?.ok || keyValidating} className="bg-deep-blue text-white" data-testid="stripe-save-key-btn">
                      Save & Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    {status.key_configured && (
                      <Button variant="ghost" onClick={() => setStep(1)} data-testid="stripe-skip-key-btn">
                        Skip <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ============ STEP 1: ACCOUNT STATUS ============ */}
            {step === 1 && (
              <div className="space-y-4" data-testid="stripe-step-account">
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-slate-700">
                  <strong>Step 2 of 4 — Account Status.</strong> Verify your Stripe account is activated and ready to accept charges.
                </div>

                {!acct ? (
                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <div className="flex items-center gap-2 text-red-800 font-medium"><XCircle className="w-4 h-4" /> Could not connect to Stripe</div>
                    <p className="text-xs text-slate-600 mt-1">Go back to Step 1 and check your API key.</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-slate-50 border border-slate-200 rounded p-4 space-y-1 text-sm" data-testid="stripe-account-info">
                      <div><strong>Account:</strong> {acct.name || "—"} <code className="text-xs text-slate-500 ml-1">{acct.id}</code></div>
                      {acct.email && <div><strong>Email:</strong> {acct.email}</div>}
                      <div><strong>Country:</strong> {acct.country || "—"}</div>
                    </div>
                    <div className="border border-slate-200 rounded p-3">
                      <CheckRow ok={acct.details_submitted} label="Activation details submitted" detail={acct.details_submitted ? "All onboarding info has been provided." : "Complete the activation form at dashboard.stripe.com."} />
                      <CheckRow ok={acct.charges_enabled} label="Charges enabled" detail={acct.charges_enabled ? "Account can accept real payments." : "Stripe is still reviewing your account or requires more info."} />
                      <CheckRow ok={acct.payouts_enabled} label="Payouts enabled (bank verified)" detail={acct.payouts_enabled ? "Funds will deposit to your bank automatically." : "Add and verify a bank account in Stripe settings."} />
                    </div>
                    {acct.requirements_due?.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded p-3" data-testid="stripe-requirements-due">
                        <div className="flex items-center gap-2 font-medium text-amber-900 text-sm"><AlertTriangle className="w-4 h-4" /> Stripe needs more information from you</div>
                        <ul className="text-xs text-amber-800 mt-2 list-disc list-inside space-y-0.5">
                          {acct.requirements_due.map(r => <li key={r}>{r}</li>)}
                        </ul>
                        <a href="https://dashboard.stripe.com/account" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-700 underline mt-2">Open Stripe to resolve <ExternalLink className="w-3 h-3" /></a>
                      </div>
                    )}
                  </>
                )}

                <div className="flex justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(0)}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
                  <Button onClick={() => setStep(2)} className="bg-deep-blue text-white" data-testid="stripe-account-next-btn">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </div>
            )}

            {/* ============ STEP 2: WEBHOOK ============ */}
            {step === 2 && (
              <div className="space-y-4" data-testid="stripe-step-webhook">
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-slate-700">
                  <strong>Step 3 of 4 — Webhook.</strong> Register a webhook so Stripe notifies your app when payments succeed (so tickets generate instantly).
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
                  <strong>Detected target URL:</strong> <code className="text-xs break-all">{status.target_webhook_url}</code>
                </div>

                <div>
                  <Label className="text-sm font-medium">Override target URL (optional)</Label>
                  <Input
                    type="url"
                    placeholder="https://www.bluevsblue.org/api/webhook/stripe"
                    value={webhookUrlOverride}
                    onChange={(e) => setWebhookUrlOverride(e.target.value)}
                    className="font-mono text-xs mt-1"
                    data-testid="stripe-webhook-url-override"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave blank to use the detected URL above. Set this if you want to point the webhook at your <strong>production</strong> domain (e.g. https://www.bluevsblue.org/api/webhook/stripe).</p>
                </div>

                <div className="border border-slate-200 rounded p-3">
                  <CheckRow
                    ok={webhookConfigured}
                    label={webhookConfigured ? "Webhook is registered with Stripe" : "Webhook NOT registered for this URL"}
                    detail={webhookConfigured ? "Stripe will POST events to your app automatically." : "Click 'Auto-create webhook' to register it in one click."}
                  />
                  <CheckRow
                    ok={status.webhook_secret_saved}
                    label="Signing secret saved"
                    detail={status.webhook_secret_saved ? "Webhook signatures are being verified." : "Auto-creating below will save the secret automatically."}
                  />
                </div>

                {status.webhooks?.length > 0 && (
                  <div className="text-xs text-slate-600" data-testid="stripe-existing-webhooks">
                    <div className="font-medium mb-1">All Stripe webhooks on this account ({status.webhooks.length}):</div>
                    <ul className="space-y-1">
                      {status.webhooks.map(w => (
                        <li key={w.id} className={`flex items-center gap-2 ${w.is_target ? "text-deep-blue font-medium" : "text-slate-500"}`}>
                          {w.is_target ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <Webhook className="w-3.5 h-3.5" />}
                          <code className="text-xs truncate flex-1">{w.url}</code>
                          <span className="text-xs">({w.enabled_events?.length || 0} events)</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {webhookResult && webhookResult.success && (
                  <div className="bg-green-50 border border-green-200 rounded p-3 text-sm space-y-1" data-testid="stripe-webhook-result">
                    <div className="flex items-center gap-2 text-green-800 font-medium"><CheckCircle2 className="w-4 h-4" /> Webhook created successfully</div>
                    <div className="text-xs text-slate-600">ID: <code>{webhookResult.id}</code></div>
                    <div className="text-xs text-slate-600">Events: {(webhookResult.enabled_events || []).join(", ")}</div>
                    {webhookResult.secret_saved && <div className="text-xs text-green-700">✓ Signing secret saved to database</div>}
                  </div>
                )}

                <div className="flex justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
                  <div className="flex gap-2">
                    <Button onClick={createWebhook} disabled={creatingWebhook} variant={webhookConfigured ? "outline" : "default"} className={webhookConfigured ? "" : "bg-deep-blue text-white"} data-testid="stripe-create-webhook-btn">
                      {creatingWebhook ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Webhook className="w-4 h-4 mr-2" />}
                      {webhookConfigured ? "Re-create webhook" : "Auto-create webhook"}
                    </Button>
                    <Button onClick={() => setStep(3)} className="bg-deep-blue text-white" data-testid="stripe-webhook-next-btn">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
                  </div>
                </div>
              </div>
            )}

            {/* ============ STEP 3: TEST CHARGE ============ */}
            {step === 3 && (
              <div className="space-y-4" data-testid="stripe-step-test">
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-slate-700">
                  <strong>Step 4 of 4 — Test Charge.</strong> Confirm end-to-end connectivity. {mode === "live" ? "In LIVE mode, this creates a $0.50 PaymentIntent and cancels it immediately (no real charge)." : "In TEST mode, this creates and refunds a $0.50 charge using Stripe's test card."}
                </div>

                {testResult && (
                  <div className={`rounded p-4 text-sm ${testResult.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`} data-testid="stripe-test-result">
                    {testResult.success ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 font-medium text-green-800"><ShieldCheck className="w-4 h-4" /> Stripe is connected and working</div>
                        <div className="text-xs text-slate-700">{testResult.message}</div>
                        {testResult.payment_intent_id && <div className="text-xs text-slate-500">PaymentIntent: <code>{testResult.payment_intent_id}</code></div>}
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 font-medium text-red-800"><XCircle className="w-4 h-4" /> Test failed</div>
                        <div className="text-xs text-slate-700">{testResult.error}</div>
                      </div>
                    )}
                  </div>
                )}

                {mode === "live" && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                    <strong>Live mode reminder:</strong> after this passes, run a real $1 ticket purchase on your live site with your own card to fully verify the customer flow, then refund yourself from the Stripe dashboard.
                  </div>
                )}

                <div className="flex justify-between pt-2">
                  <Button variant="ghost" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
                  <div className="flex gap-2">
                    <Button onClick={runTestCharge} disabled={testing} className="bg-deep-blue text-white" data-testid="stripe-run-test-btn">
                      {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                      {mode === "live" ? "Run Live Connectivity Test" : "Run $0.50 Test Charge"}
                    </Button>
                    <Button onClick={() => { fetchStatus(); onOpenChange(false); toast.success("Stripe setup wizard closed"); }} variant="outline" data-testid="stripe-finish-btn">Finish</Button>
                  </div>
                </div>
              </div>
            )}

            {/* ============ OVERALL STATUS FOOTER ============ */}
            {status.checks && (
              <div className="border-t border-slate-200 mt-6 pt-4" data-testid="stripe-status-summary">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-semibold">Overall Status</div>
                <div className="border border-slate-200 rounded">
                  {status.checks.map(c => <CheckRow key={c.id} ok={c.ok} label={c.label} detail={c.detail} />)}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
